import { useEffect, useState, useCallback, Fragment } from 'react';
import { toast } from 'sonner';
import {
  Receipt, CaretLeft, CaretRight, Calendar, Lock, LockOpen, MagnifyingGlass,
  CheckCircle, WarningCircle, PencilSimple, TrashSimple, ArrowsClockwise, CaretDown, CaretUp, MicrosoftExcelLogo,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth, canEditShizai } from '@/auth/AuthContext';
import { shizaiApi, yen } from './shizaiApi';

const NONE = '__none__';

function fmtMonth(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}
function fmtJaMonth(iso) {
  if (!iso || !/^\d{4}-\d{2}$/.test(iso)) return iso;
  const [y, m] = iso.split('-');
  return `${y}年${Number(m)}月`;
}
function shiftMonth(iso, delta) {
  const [y, m] = iso.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return fmtMonth(d);
}

const STATUS_META = {
  match:       { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: '一致',   Icon: CheckCircle,   dot: 'bg-emerald-500' },
  over:        { badge: 'bg-amber-50 text-amber-700 border-amber-200',       label: '過剰請求', Icon: WarningCircle, dot: 'bg-amber-500' },
  short:       { badge: 'bg-rose-50 text-rose-700 border-rose-200',          label: '請求金額不足', Icon: WarningCircle, dot: 'bg-rose-500' },
  not_entered: { badge: 'bg-zinc-100 text-zinc-500 border-zinc-200',         label: '未入力',   Icon: Receipt,       dot: 'bg-zinc-400' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_entered;
  return (
    <Badge variant="outline" className={`${m.badge} font-medium`} data-testid={`inv-status-${status}`}>
      <m.Icon size={12} weight="bold" className="mr-1" />
      {m.label}
    </Badge>
  );
}

/**
 * 業者請求 照合 — Milestone C (Invoice Reconciliation).
 * Row per supplier for the picked month: shows 検収金額(税込) vs 請求金額(税込) diff.
 * Inline edit of invoice, expand-row drill-down of 検収明細, 月次締め toggle.
 */
export default function InvoiceReconciliation() {
  const { user } = useAuth();
  const canWrite = canEditShizai(user);
  const [month, setMonth] = useState(() => fmtMonth(new Date()));
  const [deptId, setDeptId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [editRow, setEditRow] = useState(null);
  const [closeConfirm, setCloseConfirm] = useState(false);
  const [closeAck, setCloseAck] = useState(false);
  const [reopenConfirm, setReopenConfirm] = useState(false);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (deptId) params.department_id = deptId;
      const d = await shizaiApi.invoices(params);
      setData(d);
      setDetailCache({});
      setExpanded(null);
    } catch (e) {
      toast.error('データ取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month, deptId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { shizaiApi.departments().then(setDepartments).catch(() => {}); }, []);

  const toggleExpand = async (row) => {
    const id = row.invoice_id || `virtual:${row.supplier_id}:${month}`;
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!detailCache[id]) {
      try {
        const d = await shizaiApi.invoiceDetail(id);
        setDetailCache((c) => ({ ...c, [id]: d }));
      } catch (e) {
        toast.error('明細取得に失敗しました');
      }
    }
  };

  const unresolvedMissing = (data?.rows || []).filter((r) => r.status === 'not_entered');
  const unresolvedMismatch = (data?.rows || []).filter((r) => r.status === 'over' || r.status === 'short');
  const hasUnresolved = unresolvedMissing.length + unresolvedMismatch.length > 0;

  const closeMonth = async () => {
    try {
      await shizaiApi.closeMonth(month, hasUnresolved);
      toast.success(`${fmtJaMonth(month)} を締めました`);
      setCloseConfirm(false);
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === 'object' && det.code === 'unresolved') {
        toast.error(`${det.message}。最新の状態を再読込しました。`);
        setCloseConfirm(false);
        load();
      } else {
        toast.error(typeof det === 'string' ? det : '締めに失敗しました');
      }
    }
  };
  const reopenMonth = async () => {
    try {
      await shizaiApi.reopenMonth(month);
      toast.success(`${fmtJaMonth(month)} の締めを解除しました`);
      setReopenConfirm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '解除に失敗しました');
    }
  };

  const rows = (data?.rows || [])
    .filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (r.supplier_short || '').toLowerCase().includes(q)
          || (r.supplier_name || '').toLowerCase().includes(q)
          || (r.supplier_code || '').toLowerCase().includes(q)
          || (r.invoice_no || '').toLowerCase().includes(q);
      }
      return true;
    });

  const closed = !!data?.closed;
  const s = data?.summary;
  const canEditWhenOpen = canWrite && !closed;

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-invoices">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <Receipt size={26} weight="duotone" className="text-rose-500" />
            業者請求 照合
          </h1>
          <p className="text-sm text-zinc-500 mt-1">仕入先から届いた月次請求書の金額を、システムの検収金額と照合します。</p>
        </div>
      </div>

      {/* Controls: month picker + dept + close btn */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5">
              <Calendar size={13} weight="duotone" className="text-rose-500" /> 対象月
            </Label>
            <div className="flex items-center gap-1 mt-1.5" data-testid="inv-month-picker">
              <Button variant="outline" size="sm" onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid="inv-month-prev" className="h-10 w-10 p-0">
                <CaretLeft size={16} weight="bold" />
              </Button>
              <div className="w-40 text-center h-10 rounded-md border border-zinc-200 bg-zinc-50 flex items-center justify-center font-semibold text-zinc-800" data-testid="inv-month-label">
                {fmtJaMonth(month)}
              </div>
              <Button variant="outline" size="sm" onClick={() => setMonth((m) => shiftMonth(m, 1))} data-testid="inv-month-next" className="h-10 w-10 p-0">
                <CaretRight size={16} weight="bold" />
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-600">部門で絞込</Label>
            <Select value={deptId || NONE} onValueChange={(v) => setDeptId(v === NONE ? '' : v)}>
              <SelectTrigger data-testid="inv-dept-select" className="h-10 w-44 mt-1.5"><SelectValue placeholder="全部門" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>全部門</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={load} className="h-10" data-testid="inv-refresh-btn">
              <ArrowsClockwise size={16} weight="bold" className="mr-1.5" /> 再読込
            </Button>
            {closed ? (
              <Button
                onClick={() => setReopenConfirm(true)}
                disabled={!isAdmin}
                data-testid="inv-reopen-btn"
                className="h-10 bg-amber-600 hover:bg-amber-700 disabled:opacity-40"
                title={isAdmin ? '' : '管理者のみ締めを解除できます'}
              >
                <LockOpen size={16} weight="bold" className="mr-1.5" />
                締めを解除
              </Button>
            ) : (
              <Button
                onClick={() => { setCloseAck(false); setCloseConfirm(true); }}
                disabled={!canWrite}
                data-testid="inv-close-btn"
                className="h-10 bg-rose-600 hover:bg-rose-700 disabled:opacity-40"
              >
                <Lock size={16} weight="bold" className="mr-1.5" />
                この月を締める
              </Button>
            )}
          </div>
        </div>

        {closed && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-700" data-testid="inv-closed-banner">
            <Lock size={16} weight="fill" />
            <span>{fmtJaMonth(month)} は締め済みです。この月の検収登録・請求編集はできません。</span>
            {data?.close_info?.closed_by && (
              <span className="ml-auto text-xs text-rose-500">
                {data.close_info.closed_by} · {String(data.close_info.closed_at || '').slice(0, 10)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="inv-summary">
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3">
            <p className="text-[10px] font-semibold uppercase text-zinc-500">仕入先数</p>
            <p className="text-xl font-bold text-zinc-900 tabular-nums mt-0.5">{s.supplier_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3">
            <p className="text-[10px] font-semibold uppercase text-zinc-500">検収合計</p>
            <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5" data-testid="inv-sum-accrued">{yen(s.total_accrued_incl)}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3">
            <p className="text-[10px] font-semibold uppercase text-zinc-500">請求合計</p>
            <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5" data-testid="inv-sum-invoiced">{yen(s.total_invoiced_incl)}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3">
            <p className="text-[10px] font-semibold uppercase text-zinc-500">差異合計</p>
            <p className={`text-lg font-bold tabular-nums mt-0.5 ${s.diff_total === 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="inv-sum-diff">
              {yen(s.diff_total)}
            </p>
          </div>
          <button type="button" onClick={() => setStatusFilter(statusFilter === 'match' ? 'all' : 'match')}
                  className={`bg-white rounded-xl border shadow-sm p-3 text-left transition-colors ${statusFilter === 'match' ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-zinc-200 hover:bg-emerald-50/30'}`}
                  data-testid="inv-filter-match">
            <p className="text-[10px] font-semibold uppercase text-emerald-600">一致</p>
            <p className="text-xl font-bold text-emerald-700 tabular-nums mt-0.5">{s.match_count}</p>
          </button>
          <button type="button" onClick={() => setStatusFilter(statusFilter === 'mismatch' ? 'all' : 'mismatch')}
                  className={`bg-white rounded-xl border shadow-sm p-3 text-left transition-colors ${statusFilter === 'mismatch' ? 'border-rose-500 ring-1 ring-rose-200' : 'border-zinc-200 hover:bg-rose-50/30'}`}
                  data-testid="inv-filter-mismatch">
            <p className="text-[10px] font-semibold uppercase text-rose-600">要確認</p>
            <p className="text-xl font-bold text-rose-700 tabular-nums mt-0.5">{s.mismatch_count + s.missing_count}</p>
            <p className="text-[10px] text-zinc-400">差異 {s.mismatch_count} · 未入力 {s.missing_count}</p>
          </button>
        </div>
      )}

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-72">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="仕入先で検索..." className="h-9 pl-8" data-testid="inv-search" />
        </div>
        <div className="flex items-center gap-1 text-xs">
          {['all', 'match', 'over', 'short', 'not_entered'].map((k) => (
            <button key={k} onClick={() => setStatusFilter(k === statusFilter ? 'all' : k)}
                    data-testid={`inv-status-filter-${k}`}
                    className={`px-2.5 py-1.5 rounded-md font-medium transition-colors ${statusFilter === k ? 'bg-zinc-800 text-white' : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'}`}>
              {k === 'all' ? 'すべて' : (STATUS_META[k]?.label || k)}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-zinc-400">{rows.length}件</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-left text-xs font-semibold text-zinc-600">
                <th className="w-8"></th>
                <th className="px-3 py-2.5">仕入先</th>
                <th className="px-3 py-2.5 text-right">検収金額 (税込)</th>
                <th className="px-3 py-2.5 text-right">請求金額 (税込)</th>
                <th className="px-3 py-2.5 text-right">差異</th>
                <th className="px-3 py-2.5">状態</th>
                <th className="px-3 py-2.5">請求書No.</th>
                <th className="px-3 py-2.5 w-24">操作</th>
              </tr>
            </thead>
            <tbody data-testid="inv-table-body">
              {loading && rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-zinc-400">読み込み中...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-zinc-400">この期間の検収データはありません</td></tr>
              ) : rows.map((r) => {
                const rowId = r.invoice_id || `virtual:${r.supplier_id}:${month}`;
                const isExp = expanded === rowId;
                const det = detailCache[rowId];
                return (
                  <Fragment key={rowId}>
                    <tr className="border-b border-zinc-200 hover:bg-zinc-50/50 transition-colors" data-testid={`inv-row-${r.supplier_id}`}>
                      <td className="px-2">
                        <button onClick={() => toggleExpand(r)} className="p-1 rounded hover:bg-zinc-200 text-zinc-500" data-testid={`inv-expand-${r.supplier_id}`}>
                          {isExp ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-zinc-800">{r.supplier_short}</div>
                        <div className="text-[11px] text-zinc-400 truncate max-w-[240px]">
                          {r.supplier_name}
                          {r.qualified ? <span className="ml-1.5 inline-flex px-1.5 py-0 rounded bg-sky-50 text-sky-700 text-[10px] font-mono">適格</span> : null}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-zinc-700">{yen(r.accrued_incl)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-zinc-900">
                        {r.invoice_incl_tax != null ? yen(r.invoice_incl_tax) : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${r.status === 'match' ? 'text-emerald-700' : (r.status === 'not_entered' ? 'text-zinc-300' : 'text-rose-700')}`} data-testid={`inv-diff-${r.supplier_id}`}>
                        {r.diff != null ? yen(r.diff) : '—'}
                      </td>
                      <td className="px-3 py-2.5"><StatusBadge status={r.status} /></td>
                      <td className="px-3 py-2.5 text-xs text-zinc-500 font-mono">{r.invoice_no || '—'}</td>
                      <td className="px-3 py-2.5">
                        {canEditWhenOpen && (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setEditRow(r)} data-testid={`inv-edit-${r.supplier_id}`}>
                              <PencilSimple size={12} className="mr-1" />
                              {r.invoice_id ? '編集' : '入力'}
                            </Button>
                            {r.invoice_id && (
                              <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" onClick={async () => {
                                if (!window.confirm(`${r.supplier_short} の請求データを削除しますか？`)) return;
                                try {
                                  await shizaiApi.deleteInvoice(r.invoice_id);
                                  toast.success('削除しました');
                                  load();
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || '削除に失敗しました');
                                }
                              }} data-testid={`inv-delete-${r.supplier_id}`}>
                                <TrashSimple size={12} />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExp && (
                      <tr className="bg-zinc-50/50" data-testid={`inv-detail-${r.supplier_id}`}>
                        <td colSpan={8} className="px-4 py-3">
                          {!det ? (
                            <p className="text-xs text-zinc-400 py-4 text-center">明細を読み込み中...</p>
                          ) : det.lines.length === 0 ? (
                            <p className="text-xs text-zinc-400 py-4 text-center">検収明細はありません</p>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-zinc-500">検収明細 ({det.lines.length}件 · 合計 {yen(det.total_incl_tax)})</p>
                              <div className="rounded-md border border-zinc-200 bg-white overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead className="bg-zinc-100 text-zinc-500">
                                    <tr>
                                      <th className="px-2 py-1.5 text-left">検収日</th>
                                      <th className="px-2 py-1.5 text-left">発注No.</th>
                                      <th className="px-2 py-1.5 text-left">品番</th>
                                      <th className="px-2 py-1.5 text-left">品名</th>
                                      <th className="px-2 py-1.5 text-right">数量</th>
                                      <th className="px-2 py-1.5 text-right">単価</th>
                                      <th className="px-2 py-1.5 text-right">検収金額</th>
                                      <th className="px-2 py-1.5 text-right">税込</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {det.lines.map((ln, i) => (
                                      <tr key={`${r.invoice_id || r.supplier_id}-ln-${i}`} className="border-t border-zinc-200">
                                        <td className="px-2 py-1.5 text-zinc-600">{ln.received_at || '—'}</td>
                                        <td className="px-2 py-1.5 font-mono text-zinc-600">{ln.order_no}</td>
                                        <td className="px-2 py-1.5 font-mono text-zinc-700">{ln.part_no}</td>
                                        <td className="px-2 py-1.5 text-zinc-700 truncate max-w-[240px]">{ln.name}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">{ln.received_qty} {ln.uom}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">{yen(ln.unit_price)}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">{yen(ln.amount)}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{yen(ln.incl_tax)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit dialog */}
      <InvoiceEditDialog
        open={!!editRow}
        row={editRow}
        month={month}
        onClose={() => setEditRow(null)}
        onSaved={() => { setEditRow(null); load(); }}
      />

      {/* Close month confirm */}
      <Dialog open={closeConfirm} onOpenChange={(o) => !o && setCloseConfirm(false)}>
        <DialogContent aria-describedby={undefined} data-testid="inv-close-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock size={22} className="text-rose-600" /> 月を締める</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>{fmtJaMonth(month)} を締めますか？</p>
            {hasUnresolved ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="inv-close-warning">
                <p className="font-bold text-amber-800 flex items-center gap-1.5">
                  <WarningCircle size={16} weight="fill" />
                  未解決の項目があります
                </p>
                {unresolvedMissing.length > 0 && (
                  <div className="text-xs text-amber-800">
                    <span className="font-semibold">未入力 {unresolvedMissing.length}件: </span>
                    {unresolvedMissing.slice(0, 8).map((r) => r.supplier_short).join('、')}
                    {unresolvedMissing.length > 8 ? ` 他${unresolvedMissing.length - 8}件` : ''}
                  </div>
                )}
                {unresolvedMismatch.length > 0 && (
                  <div className="text-xs text-amber-800">
                    <span className="font-semibold">差異あり {unresolvedMismatch.length}件: </span>
                    {unresolvedMismatch.slice(0, 8).map((r) => `${r.supplier_short}（${yen(r.diff)}）`).join('、')}
                    {unresolvedMismatch.length > 8 ? ` 他${unresolvedMismatch.length - 8}件` : ''}
                  </div>
                )}
                <label className="flex items-start gap-2 pt-1 cursor-pointer select-none text-xs text-zinc-700">
                  <input
                    type="checkbox"
                    checked={closeAck}
                    onChange={(e) => setCloseAck(e.target.checked)}
                    data-testid="inv-close-ack"
                    className="mt-0.5 h-4 w-4 accent-rose-600"
                  />
                  <span>未解決の項目があることを確認のうえ、この月を締めます</span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="inv-close-allclear">
                すべての仕入先が入力済み・一致しています。
              </p>
            )}
            <p className="text-xs text-zinc-500">締めた月には検収登録・請求編集ができなくなります。締めの解除は管理者のみ可能です。</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseConfirm(false)}>キャンセル</Button>
            <Button
              onClick={closeMonth}
              disabled={hasUnresolved && !closeAck}
              className="bg-rose-600 hover:bg-rose-700 disabled:opacity-40"
              data-testid="inv-close-confirm"
            >
              <Lock size={14} weight="bold" className="mr-1.5" /> 締める
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen confirm */}
      <Dialog open={reopenConfirm} onOpenChange={(o) => !o && setReopenConfirm(false)}>
        <DialogContent aria-describedby={undefined} data-testid="inv-reopen-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><LockOpen size={22} className="text-amber-600" /> 締めを解除</DialogTitle></DialogHeader>
          <p className="text-sm">{fmtJaMonth(month)} の締めを解除しますか？</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenConfirm(false)}>キャンセル</Button>
            <Button onClick={reopenMonth} className="bg-amber-600 hover:bg-amber-700" data-testid="inv-reopen-confirm">
              <LockOpen size={14} weight="bold" className="mr-1.5" /> 解除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InvoiceEditDialog({ open, row, month, onClose, onSaved }) {
  const [form, setForm] = useState({ invoice_amount: '', invoice_no: '', invoice_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      setForm({
        invoice_amount: row.invoice_incl_tax != null ? String(row.invoice_incl_tax) : String(Math.round(row.accrued_incl)),
        invoice_no: row.invoice_no || '',
        invoice_date: row.invoice_date || '',
        notes: row.notes || '',
      });
    }
  }, [row]);

  const save = async () => {
    if (!row) return;
    const amt = Number(form.invoice_amount);
    if (!(amt >= 0)) { toast.error('金額を正しく入力してください'); return; }
    setSaving(true);
    try {
      const body = {
        supplier_id: row.supplier_id,
        month,
        invoice_amount: amt,
        invoice_no: form.invoice_no || null,
        invoice_date: form.invoice_date || null,
        notes: form.notes || null,
      };
      if (row.invoice_id) {
        await shizaiApi.updateInvoice(row.invoice_id, body);
      } else {
        await shizaiApi.createInvoice(body);
      }
      toast.success('保存しました');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;
  const diff = Number(form.invoice_amount || 0) - (row.accrued_incl || 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="inv-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={22} weight="duotone" className="text-rose-500" />
            {row.supplier_short} · {fmtJaMonth(month)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 grid grid-cols-3 gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase text-zinc-500">検収金額 (税込)</p>
              <p className="text-lg font-bold text-zinc-800 tabular-nums">{yen(row.accrued_incl)}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase text-zinc-500">請求金額 (税込)</p>
              <p className="text-lg font-bold text-zinc-800 tabular-nums">{yen(Number(form.invoice_amount || 0))}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase text-zinc-500">差異</p>
              <p className={`text-lg font-bold tabular-nums ${diff === 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="inv-edit-diff">{yen(diff)}</p>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-600">請求金額 (税込) ＊</Label>
            <Input type="number" min="0" step="1" value={form.invoice_amount}
                   onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })}
                   className="h-11 mt-1 text-lg font-semibold tabular-nums" data-testid="inv-edit-amount" />
            <p className="text-[11px] text-zinc-400 mt-1">税込金額を入力してください（消費税10%で自動分解されます）</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">請求書No.</Label>
              <Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })}
                     className="h-10 mt-1 font-mono" data-testid="inv-edit-no" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">請求日</Label>
              <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                     className="h-10 mt-1" data-testid="inv-edit-date" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-600">備考</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="mt-1" rows={2} data-testid="inv-edit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="inv-edit-save">
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
